import { ChevronDown, ChevronRight, Images } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { ALBUM_GROUPS, type AlbumGroup } from "@/lib/enums";
import { photoFileUrl } from "@/lib/photo-files";
import type { Album } from "@/schemas/photos";

/** Anything a screen adds under a card, outside its button. */
type AlbumFooter = (album: Album) => ReactNode;

function AlbumCard({
  album,
  onOpen,
  footer,
}: {
  album: Album;
  onOpen: (album: Album) => void;
  footer?: AlbumFooter;
}) {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-card transition-shadow hover:shadow-md">
      <button
        type="button"
        data-testid="album-card"
        onClick={() => onOpen(album)}
        className="group block w-full text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <span className="flex aspect-video items-center justify-center overflow-hidden bg-muted">
          {album.coverPhotoId ? (
            <img
              src={photoFileUrl(album.coverPhotoId, "thumb")}
              alt=""
              loading="lazy"
              decoding="async"
              className="size-full object-cover"
            />
          ) : (
            <Images className="size-8 text-muted-foreground/50" />
          )}
        </span>
        <span className="block p-3">
          <span className="line-clamp-2 text-sm font-medium">{album.name}</span>
          <span className="mt-1 block text-xs text-muted-foreground tabular-nums">
            {album.photoCount.toLocaleString("en-US")} {album.photoCount === 1 ? "photo" : "photos"}
          </span>
        </span>
      </button>
      {footer?.(album)}
    </div>
  );
}

function AlbumGroupSection({
  group,
  albums,
  onOpen,
  footer,
}: {
  group: AlbumGroup;
  albums: Album[];
  onOpen: (album: Album) => void;
  footer?: AlbumFooter;
}) {
  const [open, setOpen] = useState(true);
  const Icon = open ? ChevronDown : ChevronRight;
  const photoCount = albums.reduce((sum, album) => sum + album.photoCount, 0);

  return (
    <section>
      <Button
        variant="ghost"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="-ml-2 mb-2 h-auto gap-2 px-2 py-1.5"
      >
        <Icon className="size-4" />
        <span className="font-display text-base font-semibold">{group}</span>
        <span className="text-xs font-normal text-muted-foreground tabular-nums">
          {albums.length} {albums.length === 1 ? "album" : "albums"},{" "}
          {photoCount.toLocaleString("en-US")} photos
        </span>
      </Button>
      {open && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
          {albums.map((album) => (
            <AlbumCard key={album.id} album={album} onOpen={onOpen} footer={footer} />
          ))}
        </div>
      )}
    </section>
  );
}

/** Albums by group, in import order. The API leaves out albums with no photos. */
export function AlbumList({
  albums,
  onOpen,
  footer,
}: {
  albums: Album[];
  onOpen: (album: Album) => void;
  footer?: AlbumFooter;
}) {
  return (
    <div className="space-y-6">
      {ALBUM_GROUPS.map((group) => {
        const inGroup = albums.filter((album) => album.groupName === group);
        return inGroup.length > 0 ? (
          <AlbumGroupSection
            key={group}
            group={group}
            albums={inGroup}
            onOpen={onOpen}
            footer={footer}
          />
        ) : null;
      })}
    </div>
  );
}
